import { Fragment } from 'react'
import { useTheme } from '@emotion/react'
import clsx from 'clsx'
import PropTypes from 'prop-types'
import { graphql } from 'gatsby'
import {
  Container,
  Section,
  SectionTitle,
  Row,
  RowColumn,
} from 'chaoskit/src/components'
import { StylesContainerVariables } from 'chaoskit/src/components/Container'
import { misc } from 'chaoskit/src/assets/styles/utility'
import { MDXRenderer } from 'gatsby-plugin-mdx'

import Data from '~components/Data'
import ButtonLink from '~components/ButtonLink'
import BaseLayout from '~layouts/BaseLayout'
import Hero, {
  HeroContent,
  HeroFormWrapper,
  HeroTitleWrapper,
  HeroTitleSub,
  HeroTitle,
  HeroFormTitle,
} from '~partials/Hero'
import StaticFooter from '~partials/footer/StaticFooter'
import CustomerLogos from '~partials/global/CustomerLogos'
import StaticFeaturedArticles from '~partials/global/StaticFeaturedArticles'
import HelmetSEO from '~partials/global/HelmetSEO'
import NodeTypeTracker from '~partials/global/NodeTypeTracker'
import MarketoForm from '~components/MarketoForm'
import track from '~helpers/track'

const StaticPageLayout = ({
  data: {
    pageData: {
      body,
      frontmatter: {
        title,
        description,
        showFullNav,
        hero,
        showCustomerLogos,
        staticCopy,
      },
    },
  },
  pageContext: { nodeType },
}) => {
  const theme = useTheme()

  const heroText = () => (
    <Fragment>
      <HeroTitle as={hero.form && hero.form.embed ? 'h2' : 'h1'}>
        <Data>{hero.heading}</Data>
      </HeroTitle>
      <HeroTitleSub>
        <MDXRenderer>{hero.content}</MDXRenderer>
      </HeroTitleSub>
      {hero.cta && (hero.cta.label || hero.cta.url) ? (
        <ButtonLink
          type="primary"
          css={{
            marginTop: theme.space.large,
          }}
          url={hero.cta.url}
        >
          <Data>{hero.cta.label}</Data>
        </ButtonLink>
      ) : null}
    </Fragment>
  )

  const heroClasses = clsx({
    'u-borderBottom': !showCustomerLogos && (staticCopy || body),
  })

  return (
    <BaseLayout
      noFooter
      headerProps={{
        contrast: false,
        sticky: false,
        showNav: showFullNav,
      }}
    >
      <HelmetSEO title={title} description={description} noIndex />
      <NodeTypeTracker nodeType={nodeType} />
      <Hero compact contrast={false} className={heroClasses}>
        <HeroContent>
          <Container>
            {hero.form && (hero.form.heading || hero.form.embed) ? (
              <Row gutter={{ base: 'base', medium: 'xlarge' }}>
                <RowColumn
                  size={{ medium: 6 }}
                  css={{
                    textAlign: 'center',

                    [theme.mq.medium]: {
                      textAlign: 'left',
                    },
                  }}
                >
                  <div
                    css={{
                      [theme.mq.medium]: {
                        position: 'sticky',
                        top: theme.space.base,
                      },
                    }}
                  >
                    {heroText()}
                  </div>
                </RowColumn>
                <RowColumn size={{ medium: 6 }}>
                  <HeroFormWrapper>
                    <HeroFormTitle>{hero.form.heading}</HeroFormTitle>
                    {hero.form.embed && (
                      <MarketoForm
                        id={hero.form.embed}
                        fieldValues={{ programID: hero.form.programId }}
                        thankYouMessage={hero.form.thankYouMessage}
                        onSuccess={() => {
                          track('Form submitted', {
                            label: `marketo-${hero.form.embed}`,
                          })
                        }}
                      />
                    )}
                  </HeroFormWrapper>
                </RowColumn>
              </Row>
            ) : (
              <HeroTitleWrapper>{heroText()}</HeroTitleWrapper>
            )}
          </Container>
        </HeroContent>
      </Hero>
      {showCustomerLogos && (
        <CustomerLogos css={{ background: theme.color.panel.base }} />
      )}
      <Section
        css={{
          // Replicates `<Container />` so we can use the `:empty` check to hide section all-together if there are no direct children since rendering compiled MDX does not provide us a way to check if it's actually empty
          margin: '0 auto',
          maxWidth: StylesContainerVariables.small,
          paddingLeft: theme.space.base,
          paddingRight: theme.space.base,

          '&:empty': [misc.hide],
        }}
      >
        {(staticCopy?.heading || staticCopy?.blurb) && (
          <SectionTitle
            title={staticCopy.heading && <Data>{staticCopy.heading}</Data>}
            sub={staticCopy.blurb && <Data>{staticCopy.blurb}</Data>}
          />
        )}
        <MDXRenderer>{body}</MDXRenderer>
      </Section>

      <StaticFeaturedArticles
        css={{
          borderTop: theme.border.base,
        }}
      />
      <StaticFooter />
    </BaseLayout>
  )
}

StaticPageLayout.propTypes = {
  data: PropTypes.object.isRequired,
  pageContext: PropTypes.object.isRequired,
}

export default StaticPageLayout

export const pageQuery = graphql`
  query StaticPageSingle($slug: String!) {
    pageData: mdx(
      fields: { slug: { eq: $slug } }
      fileAbsolutePath: { regex: "/src/static-pages/" }
    ) {
      fileAbsolutePath
      body
      frontmatter {
        title
        description
        slug
        showFullNav
        hero {
          heading
          content
          cta {
            label
            url
          }
          form {
            embed
            heading
            programId
            thankYouMessage
          }
        }
        showCustomerLogos
        staticCopy {
          heading
          blurb
        }
      }
      fields {
        slug
      }
    }
  }
`
